import { Controller } from "@hotwired/stimulus";

// Shared global counter for open dialogs (modals, slideovers, and drawers)
if (!window.__openDialogCount) {
  window.__openDialogCount = 0;
}

// Reset dialog count on page navigation to prevent desync issues
// This handles cases where users navigate back/forward while dialogs are open
if (!window.__dialogCountResetBound) {
  window.__dialogCountResetBound = true;

  const resetDialogCount = () => {
    // Only reset if no dialogs are actually open
    const openDialogs = document.querySelectorAll("dialog[open]");
    if (openDialogs.length === 0) {
      window.__openDialogCount = 0;
      document.documentElement.style.removeProperty("--scrollbar-compensation");
      document.body.classList.remove(
        "modal-open",
        "slideover-open",
        "drawer-open",
      );
    } else {
      // Sync count with actual open dialogs
      window.__openDialogCount = openDialogs.length;
    }
  };

  document.addEventListener("turbo:before-cache", resetDialogCount);
  document.addEventListener("turbo:load", resetDialogCount);
}

export default class extends Controller {
  static targets = [
    "dialog",
    "handle",
    "content",
    "snapIndicator",
    "template",
    "snapContent",
  ];
  static values = {
    open: { type: Boolean, default: false },
    position: { type: String, default: "bottom" }, // "bottom" or "top"
    snapPoints: { type: Array, default: ["180px", "80%"] }, // Height positions: ["180px" = 180px tall, "50%" = half screen height]
    activeSnapPoint: { type: Number, default: 0 },
    dismissible: { type: Boolean, default: true },
    fadeFromIndex: { type: Number, default: -1 }, // how this works: -1 means no fade, 0 means fade from the first snap point, 1 means fade from the second snap point, etc.
    closeThreshold: { type: Number, default: 0.25 }, // Percentage of drawer height to trigger close
    velocityThreshold: { type: Number, default: 0.5 }, // Velocity threshold for flick gestures
    lazyLoad: { type: Boolean, default: false }, // Whether to lazy load the drawer content
    turboFrameSrc: { type: String, default: "" }, // URL for the turbo frame
    autoFocus: { type: Boolean, default: false }, // True: focus first focusable element on open. False: don't focus on anything by default. (if an element has autofocus="true", it will focus on it whether this option is true or false)
    modal: { type: Boolean, default: true }, // True: use showModal() (top layer). False: use show() (respects z-index)
  };

  connect() {
    this.boundBeforeCache = this.beforeCache.bind(this);
    document.addEventListener("turbo:before-cache", this.boundBeforeCache);

    this.isOpen = false;
    this.isOpening = false; // Add isOpening flag
    this.isDragging = false;
    this.isBouncing = false;
    this.isClosing = false; // Add flag to track closing state
    this.contentLoaded = false; // Track if lazy content has been loaded
    this.startY = 0;
    this.currentY = 0;
    this.lastY = 0;
    this.velocity = 0;
    this.lastTime = Date.now();

    // Initialize focus trapping
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    // Bind event handlers once
    this.boundHandleStart = this.handleStart.bind(this);
    this.boundHandleMove = this.handleMove.bind(this);
    this.boundHandleEnd = this.handleEnd.bind(this);

    // Add event listener for dialog close
    this.dialogTarget.addEventListener(
      "close",
      this.handleDialogClose.bind(this),
    );

    // Add event listener for escape key
    this.boundHandleEscape = this.handleEscape.bind(this);
    this.dialogTarget.addEventListener("cancel", this.boundHandleEscape);

    // Additional keydown listener for better escape key handling
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.dialogTarget.addEventListener("keydown", this.boundHandleKeydown);

    // Add resize observer for auto snap points
    this.resizeObserver = new ResizeObserver(() => {
      if (
        this.isOpen &&
        this.snapPointsValue[this.activeSnapPointValue] === "auto"
      ) {
        // Debounce resize recalculations
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          this.updateAutoHeight();
        }, 100);
      }
    });

    // Add mutation observer to watch for content changes
    this.mutationObserver = new MutationObserver(() => {
      if (
        this.isOpen &&
        !this.isClosing &&
        this.snapPointsValue[this.activeSnapPointValue] === "auto"
      ) {
        // Debounce mutation recalculations
        clearTimeout(this.mutationTimeout);
        this.mutationTimeout = setTimeout(() => {
          this.updateAutoHeight();
        }, 100);
      }
    });

    // Observe the dialog content for changes
    this.resizeObserver.observe(this.dialogTarget);

    // Start observing mutations (attributes, childList, and subtree)
    this.mutationObserver.observe(this.dialogTarget, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["style", "class", "hidden", "data-state"], // Watch for common state changes
    });

    // Also listen to window resize for viewport changes
    this.boundHandleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this.boundHandleResize);

    // Set initial styles based on position
    const initialTransform = this.positionValue === "top" ? "translateY(-100%)" : "translateY(100%)";
    this.dialogTarget.style.transform = initialTransform;
    this.dialogTarget.style.transition = "none";

    // Add custom event listeners for trigger controller pattern
    this.boundExternalOpen = (e) => {
      const src = e?.detail?.src || "";
      const reset = e?.detail?.reset ?? true;
      if (src) {
        this.turboFrameSrcValue = src;
        this.lazyLoadValue = true;
        if (reset || this._lastSrc !== src) this.contentLoaded = false;
        this._lastSrc = src;
      }
      this.open();
    };
    this.boundExternalClose = (e) => {
      e.stopPropagation();
      this.close();
    };

    this.element.addEventListener("drawer:open", this.boundExternalOpen);
    this.element.addEventListener("drawer:close", this.boundExternalClose);

    // Open if specified in data attribute (after all initialization is complete)
    if (this.openValue) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.open();
      });
    }
  }

  disconnect() {
    if (this.isOpen) {
      window.__openDialogCount = Math.max(0, window.__openDialogCount - 1);
      this.isOpen = false;

      if (window.__openDialogCount === 0) {
        document.documentElement.style.removeProperty(
          "--scrollbar-compensation",
        );
        document.body.classList.remove("modal-open", "drawer-open");
      }
    }

    document.removeEventListener("turbo:before-cache", this.boundBeforeCache);
    this.dialogTarget.removeEventListener(
      "close",
      this.handleDialogClose.bind(this),
    );
    this.dialogTarget.removeEventListener("cancel", this.boundHandleEscape);
    this.dialogTarget.removeEventListener("keydown", this.boundHandleKeydown);
    window.removeEventListener("resize", this.boundHandleResize);

    // Remove custom event listeners
    this.element.removeEventListener("drawer:open", this.boundExternalOpen);
    this.element.removeEventListener("drawer:close", this.boundExternalClose);

    // Clean up observers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    // Clear any pending timeouts
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    if (this.mutationTimeout) {
      clearTimeout(this.mutationTimeout);
    }

    this.removeEventListeners();
  }

  async open() {
    if (this.isOpen) return;

    // Pre-calculate height before opening to prevent jump
    if (this.lazyLoadValue && !this.contentLoaded && this.hasTemplateTarget) {
      await this.#preCalculateTemplateHeight();
    }

    // If lazy loading is enabled and content hasn't been loaded yet, load it now
    if (this.lazyLoadValue && !this.contentLoaded) {
      await this.#loadTemplateContent();
      this.contentLoaded = true;
    }

    window.__openDialogCount++;
    this.isOpen = true;
    this.isOpening = true; // Set opening flag

    if (window.__openDialogCount === 1) {
      const scrollbarWidth = this.getScrollbarWidth();
      if (scrollbarWidth > 0) {
        document.documentElement.style.setProperty(
          "--scrollbar-compensation",
          `${scrollbarWidth}px`,
        );
        document.body.classList.add("drawer-open");
      }
    }

    // Use showModal() for modal drawers (top layer), show() for non-modal (respects z-index)
    if (this.modalValue) {
      this.dialogTarget.showModal();
    } else {
      this.dialogTarget.show();
    }

    // Set up focus trapping
    this.setupFocusTrapping();

    // Always start from first snap point
    this.activeSnapPointValue = 0;

    // Remove focus from any focused element to prevent focus rings
    const focusedElement = this.dialogTarget.querySelector(":focus");
    if (focusedElement) {
      focusedElement.blur();
    }

    // Animate in with spring
    requestAnimationFrame(() => {
      this.dialogTarget.style.transition =
        "transform 0.5s cubic-bezier(0.32, 0.72, 0, 1)";
      this.snapToPoint(this.activeSnapPointValue);

      // Reset opening flag after animation duration
      setTimeout(() => {
        this.isOpening = false;

        // Force update height in case viewport changed during animation (e.g., mobile address bar)
        if (this.snapPointsValue[this.activeSnapPointValue] === "auto") {
          this.updateAutoHeight();
        }
      }, 500);
    });

    // Add drag listeners
    if (this.dismissibleValue) {
      this.addEventListeners();
    }
  }

  close(resetPosition = false) {
    if (!this.isOpen) return;

    this.isClosing = true; // Set closing flag
    this.dialogTarget.setAttribute("closing", "");
    this.dialogTarget.style.transition =
      "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)";

    const closeTransform = this.positionValue === "top" ? "translateY(-100%)" : "translateY(100%)";

    if (resetPosition) {
      // First animate to initial position
      this.snapToPoint(0);

      // Then close after a brief delay
      setTimeout(() => {
        this.dialogTarget.style.transform = closeTransform;
      }, 150);

      setTimeout(() => {
        this.finishClosing();
      }, 450);
    } else {
      // Close immediately
      this.dialogTarget.style.transform = closeTransform;

      setTimeout(() => {
        this.finishClosing();
      }, 300);
    }
  }

  finishClosing() {
    this.dialogTarget.removeAttribute("closing");
    this.dialogTarget.close();

    window.__openDialogCount = Math.max(0, window.__openDialogCount - 1);
    this.isOpen = false;
    this.isClosing = false; // Reset closing flag

    if (window.__openDialogCount === 0) {
      document.documentElement.style.removeProperty("--scrollbar-compensation");
      document.body.classList.remove("modal-open", "drawer-open");
    }

    this.removeEventListeners();

    // Reset to initial snap point for next opening
    this.activeSnapPointValue = 0;

    // Reset drag-related state
    this.isDragging = false;
    this.velocity = 0;
    this.startY = 0;
    this.currentY = 0;
    this.lastY = 0;
    this.initialTransform = 0;

    // If it's a template-based lazy drawer, reset its content to show loading state on next open
    // This ensures height is pre-calculated correctly every time.
    if (this.lazyLoadValue && this.hasTemplateTarget) {
      this.contentLoaded = false;
      const container = this.dialogTarget.querySelector(
        "[data-drawer-content]",
      );
      if (container) {
        container.innerHTML = `
          <div class="px-6 pb-6 pt-4 text-center">
            <div class="inline-block h-6 w-6 animate-spin rounded-full border-[3px] border-current border-t-transparent text-neutral-600 dark:text-neutral-400" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2 text-sm text-neutral-500 dark:text-neutral-400">Loading content...</p>
          </div>
        `;
      }
    }
  }

  snapToPoint(index, animate = true) {
    const snapPoint = this.snapPointsValue[index];
    if (!snapPoint) return;

    this.activeSnapPointValue = index;

    // New logic for revealing content based on snap points
    if (this.hasSnapContentTarget) {
      this.snapContentTargets.forEach((content) => {
        // Default to 0 if the attribute is missing
        const showFromIndex = parseInt(content.dataset.showFromSnapPoint, 10);
        const effectiveShowFromIndex = isNaN(showFromIndex) ? 0 : showFromIndex;

        if (index >= effectiveShowFromIndex) {
          content.classList.remove("hidden");
        } else {
          content.classList.add("hidden");
        }
      });
    }

    // Store whether we should animate
    const shouldAnimate = animate && !this.isDragging;

    // Calculate transform/height based on snap point
    const isTopDrawer = this.positionValue === "top";
    let translateY = 0;
    let height = null;

    if (snapPoint === "auto") {
      // For "auto", calculate the actual content height
      if (isTopDrawer) {
        // For top drawer, calculate height directly
        const contentWrapper = this.dialogTarget.querySelector(":scope > div");
        if (contentWrapper) {
          const windowHeight = window.innerHeight;
          const naturalHeight = contentWrapper.scrollHeight;
          height = `${Math.min(naturalHeight, windowHeight * 0.9)}px`;
        } else {
          height = "80vh";
        }
        translateY = 0;
      } else {
        translateY = this.calculateAutoTranslateY();
      }
    } else if (snapPoint.endsWith("px")) {
      if (isTopDrawer) {
        height = snapPoint;
        translateY = 0;
      } else {
        translateY = `calc(100% - ${snapPoint})`;
      }
    } else if (snapPoint.endsWith("%")) {
      // Use window.innerHeight for percentage-based snap points to account for mobile browser UI
      const percentage = parseInt(snapPoint) / 100;
      const pixelHeight = window.innerHeight * percentage;
      if (isTopDrawer) {
        height = `${pixelHeight}px`;
        translateY = 0;
      } else {
        translateY = `calc(100% - ${pixelHeight}px)`;
      }
    }

    // Apply transform/height with or without animation
    if (shouldAnimate) {
      this.dialogTarget.style.transition =
        "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1), height 0.3s cubic-bezier(0.32, 0.72, 0, 1)";
    } else {
      this.dialogTarget.style.transition = "none";
    }

    if (isTopDrawer && height !== null) {
      this.dialogTarget.style.height = height;
      this.dialogTarget.style.transform = `translateY(${translateY})`;
    } else {
      this.dialogTarget.style.transform = `translateY(${translateY})`;
    }

    // Update snap indicator if present
    if (this.hasSnapIndicatorTarget) {
      this.snapIndicatorTarget.textContent =
        snapPoint === "auto"
          ? `${Math.min(this.dialogTarget.scrollHeight, window.innerHeight * 0.9)}px`
          : snapPoint;
    }

    // Update scrollable area height based on snap point
    this.updateScrollableHeight(snapPoint);

    // Handle fade effect
    if (this.fadeFromIndexValue >= 0 && index >= this.fadeFromIndexValue) {
      const fadeAmount =
        (index - this.fadeFromIndexValue) /
        (this.snapPointsValue.length - this.fadeFromIndexValue - 1);
      this.dialogTarget.style.opacity = 1 - fadeAmount * 0.4;
    } else {
      this.dialogTarget.style.opacity = 1;
    }
  }

  // Touch and mouse event handlers
  addEventListeners() {
    // Touch events
    this.dialogTarget.addEventListener("touchstart", this.boundHandleStart, {
      passive: false,
    });
    this.dialogTarget.addEventListener("touchmove", this.boundHandleMove, {
      passive: false,
    });
    this.dialogTarget.addEventListener("touchend", this.boundHandleEnd);

    // Mouse events
    this.dialogTarget.addEventListener("mousedown", this.boundHandleStart);
    document.addEventListener("mousemove", this.boundHandleMove);
    document.addEventListener("mouseup", this.boundHandleEnd);
  }

  removeEventListeners() {
    this.dialogTarget.removeEventListener("touchstart", this.boundHandleStart);
    this.dialogTarget.removeEventListener("touchmove", this.boundHandleMove);
    this.dialogTarget.removeEventListener("touchend", this.boundHandleEnd);

    this.dialogTarget.removeEventListener("mousedown", this.boundHandleStart);
    document.removeEventListener("mousemove", this.boundHandleMove);
    document.removeEventListener("mouseup", this.boundHandleEnd);
  }

  handleStart(event) {
    // Only start dragging from handle or if no handle exists
    const target = event.target;
    const isHandle = this.hasHandleTarget && this.handleTarget.contains(target);
    const hasNoHandle = !this.hasHandleTarget;

    if (!isHandle && !hasNoHandle) return;

    this.isDragging = true;
    this.dialogTarget.style.transition = "none";

    const clientY = event.type.includes("touch")
      ? event.touches[0].clientY
      : event.clientY;
    this.startY = clientY;
    this.currentY = clientY;
    this.lastY = clientY;
    this.lastTime = Date.now();

    // Reset velocity at the start of each drag
    this.velocity = 0;

    // Store the initial transform
    this.initialTransform = this.getCurrentTransformY();

    if (event.type.includes("touch")) {
      event.preventDefault();
    }
  }

  handleMove(event) {
    if (!this.isDragging) return;

    const clientY = event.type.includes("touch")
      ? event.touches[0].clientY
      : event.clientY;
    const deltaY = clientY - this.startY;

    const isTopDrawer = this.positionValue === "top";

    // For bottom drawer: dragging up expands, for top drawer: dragging down expands
    const expandDelta = isTopDrawer ? 50 : -50;
    const shouldExpand = isTopDrawer ? deltaY > expandDelta : deltaY < expandDelta;

    if (
      shouldExpand &&
      this.activeSnapPointValue < this.snapPointsValue.length - 1
    ) {
      this.isDragging = false;
      this.dialogTarget.style.transition =
        "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)";
      this.snapToNext();
      return;
    }

    // For bottom drawer: allow dragging down, for top drawer: allow dragging up
    const allowDrag = isTopDrawer ? deltaY < 0 : deltaY > 0;

    if (allowDrag) {
      this.currentY = clientY;

      // Calculate velocity
      const currentTime = Date.now();
      const timeDelta = currentTime - this.lastTime;
      this.velocity = (clientY - this.lastY) / timeDelta;

      this.lastY = clientY;
      this.lastTime = currentTime;

      // Apply transform based on initial position plus drag amount
      const newTransform = this.initialTransform + deltaY;
      this.dialogTarget.style.transform = `translateY(${newTransform}px)`;
    }

    if (event.type.includes("touch")) {
      event.preventDefault();
    }
  }

  handleEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.dialogTarget.style.transition =
      "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)";

    const deltaY = this.currentY - this.startY;
    const drawerHeight = this.dialogTarget.offsetHeight;
    const threshold = drawerHeight * this.closeThresholdValue;

    const isTopDrawer = this.positionValue === "top";

    // For bottom drawer: upward swipe expands, for top drawer: downward swipe expands
    const expandThreshold = isTopDrawer ? 30 : -30;
    const shouldExpand = isTopDrawer ? deltaY > expandThreshold : deltaY < expandThreshold;

    if (
      shouldExpand &&
      this.activeSnapPointValue < this.snapPointsValue.length - 1
    ) {
      this.snapToNext();
      return;
    }

    // For bottom drawer: downward swipe collapses, for top drawer: upward swipe collapses
    const collapseThreshold = isTopDrawer ? -30 : 30;
    const collapseMax = isTopDrawer ? -threshold : threshold;
    const shouldCollapse = isTopDrawer
      ? (deltaY < collapseThreshold && deltaY > collapseMax)
      : (deltaY > collapseThreshold && deltaY < collapseMax);

    if (this.activeSnapPointValue > 0 && shouldCollapse) {
      this.snapToPrevious();
      return;
    }

    // Check if we should close based on position or velocity
    const closeCondition = isTopDrawer
      ? (deltaY < -threshold || this.velocity < -this.velocityThresholdValue)
      : (deltaY > threshold || this.velocity > this.velocityThresholdValue);

    if (closeCondition) {
      if (this.dismissibleValue) {
        this.close(false);
      } else {
        // If not dismissible, bounce back to current snap point
        this.bounce();
        this.snapToPoint(this.activeSnapPointValue);
      }
    } else {
      // Snap back to current snap point
      this.snapToPoint(this.activeSnapPointValue);
    }
  }

  getCurrentTransformY() {
    const transform = window.getComputedStyle(this.dialogTarget).transform;
    if (transform === "none") {
      // If no transform matrix, calculate from the current position
      const rect = this.dialogTarget.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      return rect.top - (windowHeight - this.dialogTarget.offsetHeight);
    }

    const matrix = transform.match(/matrix.*\((.+)\)/);
    if (matrix) {
      const values = matrix[1].split(", ");
      return parseFloat(values[5]);
    }
    return 0;
  }

  backdropClose(event) {
    if (event.target.nodeName === "DIALOG") {
      event.stopPropagation();
      // Only close if dismissible
      if (this.dismissibleValue) {
        this.close(false);
      } else {
        // Show bounce animation when trying to dismiss
        this.bounce();
      }
    }
  }

  show() {
    this.open();
  }

  hide(event) {
    if (event) event.preventDefault();
    this.close(false);
  }

  beforeCache() {
    // Close immediately without animation during navigation
    if (this.isOpen) {
      this.finishClosing();
    }
  }

  getScrollbarWidth() {
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar";
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  handleDialogClose() {
    if (this.isOpen) {
      window.__openDialogCount = Math.max(0, window.__openDialogCount - 1);
      this.isOpen = false;

      if (window.__openDialogCount === 0) {
        document.documentElement.style.removeProperty(
          "--scrollbar-compensation",
        );
        document.body.classList.remove("modal-open", "drawer-open");
      }
    }

    this.dialogTarget.removeAttribute("closing");
  }

  // Snap to next point
  snapToNext() {
    const nextIndex = Math.min(
      this.activeSnapPointValue + 1,
      this.snapPointsValue.length - 1,
    );
    this.snapToPoint(nextIndex);
  }

  // Snap to previous point
  snapToPrevious() {
    const prevIndex = Math.max(this.activeSnapPointValue - 1, 0);
    this.snapToPoint(prevIndex);
  }

  // Handle keydown events
  handleKeydown(event) {
    if (event.key === "Escape" && !this.dismissibleValue) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.bounce();
      return false;
    }

    // Handle Tab key for focus trapping
    if (event.key === "Tab") {
      this.handleTabKey(event);
    }
  }

  // Handle escape key press (cancel event)
  handleEscape(event) {
    if (!this.dismissibleValue) {
      event.preventDefault();
      event.stopPropagation();
      this.bounce();
      return false;
    }

    // Only close if dismissible
    event.preventDefault();
    // Close with position reset
    this.close(true);
  }

  // Add bounce animation to indicate drawer won't close
  bounce() {
    // Prevent multiple bounces in quick succession
    if (this.isBouncing) return;

    this.isBouncing = true;

    // Save the current transform
    const currentTransform = this.dialogTarget.style.transform;

    // Calculate bounce distance (smaller bounce for drawers)
    const currentY = this.getCurrentTransformY();
    const bounceDistance = -20; // pixels

    // Apply quick bounce animation
    this.dialogTarget.style.transition =
      "transform 0.15s cubic-bezier(0.32, 0.72, 0, 1)";
    this.dialogTarget.style.transform = `translateY(${currentY + bounceDistance}px)`;

    setTimeout(() => {
      // Bounce back
      this.dialogTarget.style.transition =
        "transform 0.25s cubic-bezier(0.32, 0.72, 0, 1)";
      this.dialogTarget.style.transform = currentTransform;

      setTimeout(() => {
        // Reset bouncing flag after animation completes
        this.isBouncing = false;
      }, 250);
    }, 150);
  }

  // Handle window resize
  handleResize() {
    if (!this.isOpen || this.isOpening) return; // Ignore resizes during opening animation

    // For any snap point, recalculate on resize
    // Use debouncing to avoid too many recalculations
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Store current transition
      const currentTransition = this.dialogTarget.style.transition;

      // Disable transition for immediate repositioning
      this.dialogTarget.style.transition = "none";

      // Recalculate position
      this.snapToPoint(this.activeSnapPointValue);

      // Re-enable transition after a frame
      requestAnimationFrame(() => {
        this.dialogTarget.style.transition =
          currentTransition || "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)";
      });
    }, 150);
  }

  // Update scrollable area height based on snap point
  updateScrollableHeight(snapPoint) {
    // Find elements with data-drawer-scrollable attribute
    const scrollableElements = this.dialogTarget.querySelectorAll(
      "[data-drawer-scrollable]",
    );

    scrollableElements.forEach((element) => {
      // Calculate the height of non-scrollable elements dynamically
      const nonScrollableHeight = this.calculateNonScrollableHeight(element);
      let height;

      if (snapPoint.endsWith("%")) {
        // For percentage snap points, use window.innerHeight instead of vh units
        const percentage = parseInt(snapPoint) / 100;
        const pixelHeight = window.innerHeight * percentage;
        height = `calc(${pixelHeight}px - ${nonScrollableHeight}px)`;
      } else if (snapPoint.endsWith("px")) {
        // For pixel snap points
        const pixels = parseInt(snapPoint);
        height = `calc(${pixels}px - ${nonScrollableHeight}px)`;
      } else if (snapPoint === "auto") {
        // For auto, calculate based on actual content
        const contentWrapper = this.dialogTarget.querySelector(":scope > div");
        if (contentWrapper) {
          const windowHeight = window.innerHeight;
          const maxHeight = Math.min(
            contentWrapper.scrollHeight,
            windowHeight * 0.9,
          );
          height = `calc(${maxHeight}px - ${nonScrollableHeight}px)`;
        } else {
          height = `calc(80vh - ${nonScrollableHeight}px)`;
        }
      }

      element.style.maxHeight = height;
    });
  }

  // Calculate the total height of non-scrollable elements
  calculateNonScrollableHeight(scrollableElement) {
    const container =
      scrollableElement.closest(".flex.h-full.flex-col") ||
      this.dialogTarget.querySelector(":scope > div");
    if (!container) return 152; // Fallback to approximately 9.5rem

    let totalHeight = 0;

    // Get all direct children of the container
    const children = Array.from(container.children);

    children.forEach((child) => {
      // Skip the scrollable element itself
      if (child === scrollableElement || child.contains(scrollableElement))
        return;

      // Calculate the total height including margins
      const styles = window.getComputedStyle(child);
      const marginTop = parseFloat(styles.marginTop) || 0;
      const marginBottom = parseFloat(styles.marginBottom) || 0;
      const height = child.offsetHeight;

      totalHeight += height + marginTop + marginBottom;
    });

    return totalHeight;
  }

  // New method to calculate auto height translateY
  calculateAutoTranslateY() {
    // Get the content wrapper inside the dialog
    const contentWrapper = this.dialogTarget.querySelector(":scope > div");
    if (!contentWrapper) return "calc(100% - 400px)"; // Fallback

    // Get window height
    const windowHeight = window.innerHeight;

    // Temporarily remove any height constraints to measure natural height
    const originalMaxHeight = contentWrapper.style.maxHeight;
    const originalHeight = contentWrapper.style.height;
    contentWrapper.style.maxHeight = "none";
    contentWrapper.style.height = "auto";

    // Force layout recalculation
    contentWrapper.offsetHeight;

    // Measure the natural height of the content
    const naturalHeight = contentWrapper.scrollHeight;

    // Restore original styles
    contentWrapper.style.maxHeight = originalMaxHeight;
    contentWrapper.style.height = originalHeight;

    const effectiveHeight = Math.min(naturalHeight, windowHeight * 0.9);
    return `calc(100% - ${effectiveHeight}px)`;
  }

  // New method to update height when content changes
  updateAutoHeight() {
    if (!this.isOpen || this.isDragging || this.isClosing) return;

    const currentSnapPoint = this.snapPointsValue[this.activeSnapPointValue];
    if (currentSnapPoint !== "auto") return;

    // Wait for any animations to complete
    requestAnimationFrame(() => {
      // Calculate new height
      const newTranslateY = this.calculateAutoTranslateY();

      // Apply transform with smooth transition
      this.dialogTarget.style.transition =
        "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)";
      this.dialogTarget.style.transform = `translateY(${newTranslateY})`;

      // Update scrollable height
      this.updateScrollableHeight("auto");
    });
  }

  async #loadTemplateContent() {
    // Find the container in the dialog to append content to
    const container =
      this.dialogTarget.querySelector("[data-drawer-content]") ||
      this.dialogTarget;

    // Check if we should use Turbo Frame lazy loading
    if (this.turboFrameSrcValue) {
      // Look for a turbo-frame in the container
      let turboFrame = container.querySelector("turbo-frame");

      if (!turboFrame) {
        // Create a turbo-frame if it doesn't exist
        turboFrame = document.createElement("turbo-frame");
        turboFrame.id = "drawer-lazy-content";

        // Clear any loading indicators or placeholder content
        container.innerHTML = "";
        container.appendChild(turboFrame);
      }

      // Set the src to trigger the lazy load
      turboFrame.src = this.turboFrameSrcValue;

      // Wait for the turbo-frame to load
      return new Promise((resolve) => {
        const handleLoad = () => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        };

        turboFrame.addEventListener("turbo:frame-load", handleLoad);

        // Fallback timeout in case the frame doesn't load
        setTimeout(() => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        }, 5000);
      });
    } else if (this.hasTemplateTarget) {
      // Use template-based lazy loading
      const templateContent = this.templateTarget.content.cloneNode(true);

      // Clear any loading indicators or placeholder content
      const container =
        this.dialogTarget.querySelector("[data-drawer-content]") ||
        this.dialogTarget;
      container.innerHTML = "";

      // Append the template content
      container.appendChild(templateContent);
    }
  }

  async #preCalculateTemplateHeight() {
    const container =
      this.dialogTarget.querySelector("[data-drawer-content]") ||
      this.dialogTarget;
    if (!container) return;

    // Create a temporary container to measure the template content
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.visibility = "hidden";
    tempContainer.style.pointerEvents = "none";
    tempContainer.style.width = container.offsetWidth + "px";
    tempContainer.className = container.className;

    // Clone the template content into the temp container
    const tempContent = this.templateTarget.content.cloneNode(true);
    tempContainer.appendChild(tempContent);

    // Temporarily append to body to measure
    document.body.appendChild(tempContainer);

    // Get the measured height
    const measuredHeight = tempContainer.offsetHeight;

    // Remove the temporary container
    document.body.removeChild(tempContainer);

    // Pre-position the drawer based on the measured height
    const windowHeight = window.innerHeight;
    const effectiveHeight = Math.min(measuredHeight, windowHeight * 0.9);
    const translateY = `calc(100% - ${effectiveHeight}px)`;

    // Set the transform immediately to prevent jump
    this.dialogTarget.style.transform = `translateY(${translateY})`;
  }

  // Set up focus trapping for the drawer
  setupFocusTrapping() {
    // Get all focusable elements within the drawer
    this.updateFocusableElements();

    // Check for elements with native autofocus attribute first
    const autofocusElement = this.dialogTarget.querySelector("[autofocus]");
    if (autofocusElement) {
      // Always respect native autofocus, regardless of device type
      // Small delay to allow the drawer animation to start
      setTimeout(() => {
        autofocusElement.focus();
      }, 100);
    } else if (this.autoFocusValue && this.firstFocusableElement) {
      // Fall back to auto-focusing first element if no native autofocus
      // Small delay to allow the drawer animation to start
      setTimeout(() => {
        this.firstFocusableElement.focus();
      }, 100);
    }
  }

  // Update the list of focusable elements
  updateFocusableElements() {
    const focusableSelector = [
      "a[href]",
      "area[href]",
      'input:not([disabled]):not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      "details",
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]:not([contenteditable="false"])',
    ].join(",");

    this.focusableElements = Array.from(
      this.dialogTarget.querySelectorAll(focusableSelector),
    ).filter((element) => {
      // Filter out elements that are not visible or have display: none
      return (
        element.offsetWidth > 0 &&
        element.offsetHeight > 0 &&
        getComputedStyle(element).display !== "none" &&
        getComputedStyle(element).visibility !== "hidden"
      );
    });

    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1] || null;
  }

  // Handle Tab key for focus trapping
  handleTabKey(event) {
    // Update focusable elements in case the DOM has changed
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) {
      // If no focusable elements, prevent tab and keep focus on dialog
      event.preventDefault();
      return;
    }

    if (this.focusableElements.length === 1) {
      // If only one focusable element, prevent tab and keep focus on it
      event.preventDefault();
      this.firstFocusableElement.focus();
      return;
    }

    // Handle normal tab navigation with wrapping
    if (event.shiftKey) {
      // Shift+Tab: moving backwards
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }
}
